import { useEffect, useState } from "react";
import { api } from "../../api";
import { trackEvent } from "../../analytics";

const PACKAGING_OPTIONS = [
  { value: "road-tanker", label: "Road tanker (full load)" },
  { value: "iso-tanker", label: "ISO tanker / isotainer" },
  { value: "rail-rake", label: "Rail rake (bulk)" },
];

const ORDER_TYPE_OPTIONS = [
  { value: "standing", label: "Standing / recurring order" },
  { value: "one-time", label: "One-time order" },
];

export default function QuoteModal({ open, onClose, chemicalKey, chemicalName, source }) {
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    quantity: "",
    packaging: "road-tanker",
    order_type: "standing",
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [errorMsg, setErrorMsg] = useState("");
  const [reference, setReference] = useState("");

  useEffect(() => {
    if (open) {
      setStatus("idle");
      setErrorMsg("");
      setReference("");
      trackEvent("quote_modal_opened", { source, chemical: chemicalKey });
    }
  }, [open, source, chemicalKey]);

  if (!open) return null;

  function update(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function submit(e) {
    e.preventDefault();
    setStatus("sending");
    try {
      const res = await api.submitQuote({
        ...form,
        chemical: chemicalKey || null,
        source: source || "unknown",
      });
      trackEvent("quote_request_submitted", { source, chemical: chemicalKey, reference: res.reference });
      setReference(res.reference || "");
      setStatus("sent");
    } catch (err) {
      setErrorMsg(err.message || "Something went wrong — please try again.");
      setStatus("error");
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose} data-testid="quote-modal-backdrop">
      <div className="modal" onClick={(e) => e.stopPropagation()} data-testid="quote-modal">
        <button className="modal-close" onClick={onClose} aria-label="Close" data-testid="quote-modal-close">
          ×
        </button>

        {status === "sent" ? (
          <div className="modal-success" data-testid="quote-modal-success">
            <div className="eyebrow">Reference {reference}</div>
            <h3>Request sent</h3>
            <p>
              Thanks — our sales team will follow up shortly
              {chemicalName ? ` about ${chemicalName}` : ""}.
            </p>
            <button className="btn btn-brand" onClick={onClose} data-testid="quote-modal-close-success">
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="quote-form">
            <div className="eyebrow">Request a quote</div>
            <h3 style={{ marginBottom: 4 }}>
              {chemicalName ? `Pricing for ${chemicalName}` : "Talk to our sales team"}
            </h3>
            <p className="note" style={{ marginBottom: 18 }}>
              A quick note on quantity and timeline helps us respond faster.
            </p>

            <label>
              Name
              <input required value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="quote-name" />
            </label>
            <label>
              Company
              <input required value={form.company} onChange={(e) => update("company", e.target.value)} data-testid="quote-company" />
            </label>
            <div className="modal-row">
              <label>
                Work email
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  data-testid="quote-email"
                />
              </label>
              <label>
                Phone (optional)
                <input value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="quote-phone" />
              </label>
            </div>

            <label>
              Estimated quantity
              <input
                required
                placeholder="e.g. 20 MT/month, or 2 tanker loads"
                value={form.quantity}
                onChange={(e) => update("quantity", e.target.value)}
                data-testid="quote-quantity"
              />
            </label>

            <div className="modal-row">
              <label>
                Packaging
                <select value={form.packaging} onChange={(e) => update("packaging", e.target.value)} data-testid="quote-packaging">
                  {PACKAGING_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Order type
                <select value={form.order_type} onChange={(e) => update("order_type", e.target.value)} data-testid="quote-order-type">
                  {ORDER_TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <label>
              Anything else
              <textarea
                rows={3}
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                placeholder="e.g. need first delivery by mid-August, delivering to two depots"
                data-testid="quote-message"
              />
            </label>

            {status === "error" && <p className="modal-error" data-testid="quote-error">{errorMsg}</p>}

            <button className="btn btn-brand" type="submit" disabled={status === "sending"} data-testid="quote-submit">
              {status === "sending" ? "Sending…" : "Send request"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
